import React, { useEffect, useState, useRef, useCallback } from "react";
import { ChevronRight, Plus, X, Zap } from "lucide-react";
import { FiGitMerge } from "react-icons/fi";
import { SlEnergy } from "react-icons/sl";
import { progressService } from "../../../../../services/progressService";
import Silhouette from "./Silhouette";
import { MdKeyboardArrowRight } from "react-icons/md";

const ArrowTriangle = ({ direction = "right", className = "" }) => {
  const baseStyle = { width: 0, height: 0, borderTop: "7px solid transparent", borderBottom: "7px solid transparent", borderLeft: "10px solid #000", display: "inline-block" };
  return <span className={className} style={direction === "down" ? { ...baseStyle, transform: "rotate(90deg)" } : baseStyle} aria-hidden />;
};

const Toast = ({ toasts, removeToast }) => (
  <div aria-live="polite" className="fixed top-6 right-6 z-50 flex flex-col gap-3">
    {toasts.map(t => (
      <div key={t.id} className={`px-4 py-2 rounded shadow-lg ${t.type === 'error' ? 'bg-red-600 text-white' : 'bg-black text-white'}`}>
        {t.message}
        <button className="ml-3 text-xs opacity-70" onClick={() => removeToast(t.id)}>✕</button>
      </div>
    ))}
  </div>
);

const ConfirmModal = ({ isOpen, onConfirm, onCancel, energy }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">⚠️ Warning</h3>
          <p className="text-gray-700 mb-2">
            This task has <span className="font-bold text-[#54B0AF]">{energy}%</span> energy assigned.
          </p>
          <p className="text-gray-700 mb-2">
            When you add subtasks, the main task energy will be cleared and replaced with the sum of subtask energies.
          </p>
          <p className="text-gray-700 font-medium">
            Do you want to continue?
          </p>
        </div>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200  font-gotham font-medium text-gray-800 rounded hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-[#54B0AF] font-gotham font-medium text-white rounded hover:bg-[#3AAFA9] transition-colors"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};


const TaskEnergyAnalyzer = ({ userId, subsectionId, onNextStep, onPreviousStep, onDataChange, initialTasks = null }) => {
  const nextMainIdRef = useRef(8);
  const nextSubIdRef = useRef(1000);
  const nameInputRefs = useRef(new Map());
  const energyInputRefs = useRef(new Map());
  const saveTimerRef = useRef(null);
  const skipInitialAutoSave = useRef(true);
  const hasLoadedInitialData = useRef(false);

  const buildDemo = () => Array.from({ length: 5 }).map((_, i) => ({ id: i + 1, name: `Main Task ${i + 1}`, energy: null, subtasks: [], _isDemoPlaceholder: true }));

  const generateGuid = () => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
      const rand = Math.floor(Math.random() * 16);
      const val = ch === "x" ? rand : (rand & 0x3) | 0x8;
      return val.toString(16);
    });
  };

  const normalizeLoaded = useCallback((items = []) => {
    if (!Array.isArray(items) || items.length === 0) return [];
    let didAssignIds = false;
    const ensureId = (value) => {
      if (value !== null && value !== undefined && String(value).trim() !== "") {
        return value;
      }
      didAssignIds = true;
      return generateGuid();
    };

    const raw = items.map((it, idx) => {
      const id = ensureId(it.id);
      const parentId = it.parentId ?? null;
      const name = it.name ?? "";
      const energy = (it.energy === null || it.energy === "" ? null : (typeof it.energy === "number" ? it.energy : Number(it.energy)));
      
      const category = it.category ?? null;
      const delegationQuadrant = it.delegationQuadrant ?? null;
      const delegateTo = it.delegateTo ?? it.assignedTo ?? "";
      const delegationConstraint = it.delegationConstraint ?? it.constraint ?? "";
      
      const nested = Array.isArray(it.subtasks) ? it.subtasks.map((s) => ({ 
        id: ensureId(s.id), 
        name: s.name ?? "", 
        energy: (s.energy === "" || s.energy === null ? null : (typeof s.energy === "number" ? s.energy : Number(s.energy))),
        category: s.category ?? null,
        delegationQuadrant: s.delegationQuadrant ?? null,
        delegateTo: s.delegateTo ?? s.assignedTo ?? "",
        delegationConstraint: s.delegationConstraint ?? s.constraint ?? ""
      })) : null;
      
      return { id, parentId, name, energy, category, delegationQuadrant, delegateTo, delegationConstraint, nested, original: it };
    });

    const inferParentFromId = (id) => {
      try {
        const sid = String(id);
        if (sid.includes('.')) {
          const prefix = sid.split('.')[0];
          if (/^\d+$/.test(prefix)) return Number(prefix);
          return prefix;
        }
      } catch (e) {}
      return null;
    };

    const nodeMap = new Map();
    const childRelations = new Map();

    raw.forEach(r => {
      const key = String(r.id);
      if (!nodeMap.has(key)) {
        nodeMap.set(key, { 
          id: r.id, 
          name: r.name || `Task ${r.id}`, 
          energy: r.energy, 
          category: r.category,
          delegationQuadrant: r.delegationQuadrant,
          delegateTo: r.delegateTo ?? r.assignedTo ?? "",
          delegationConstraint: r.delegationConstraint ?? r.constraint ?? "",
          subtasks: [], 
          _isPlaceholder: false 
        });
      } else { 
        const existing = nodeMap.get(key); 
        existing.name = r.name || existing.name; 
        existing.energy = (r.energy === undefined ? existing.energy : r.energy);
        existing.category = r.category ?? existing.category;
        existing.delegationQuadrant = r.delegationQuadrant ?? existing.delegationQuadrant;
        existing.delegateTo = r.delegateTo ?? r.assignedTo ?? existing.delegateTo;
        existing.delegationConstraint = r.delegationConstraint ?? r.constraint ?? existing.delegationConstraint;
        existing._isPlaceholder = false; 
      }

      if (Array.isArray(r.nested) && r.nested.length > 0) {
        const pk = String(r.id);
        if (!childRelations.has(pk)) childRelations.set(pk, []);
        r.nested.forEach(ns => childRelations.get(pk).push({ 
          id: ns.id, 
          name: ns.name || `Subtask ${ns.id}`, 
          energy: ns.energy ?? null,
          category: ns.category ?? null,
          delegationQuadrant: ns.delegationQuadrant ?? null,
          delegateTo: ns.delegateTo ?? ns.assignedTo ?? "",
          delegationConstraint: ns.delegationConstraint ?? ns.constraint ?? ""
        }));
      }
    });

    raw.forEach(r => {
      if (r.parentId !== null && r.parentId !== undefined) {
        const pk = String(r.parentId);
        if (!childRelations.has(pk)) childRelations.set(pk, []);
        childRelations.get(pk).push({ 
          id: r.id, 
          name: r.name || `Subtask ${r.id}`, 
          energy: r.energy ?? null,
          category: r.category ?? null,
          delegationQuadrant: r.delegationQuadrant ?? null,
          delegateTo: r.delegateTo ?? r.assignedTo ?? "",
          delegationConstraint: r.delegationConstraint ?? r.constraint ?? ""
        });
      }
    });

    raw.forEach(r => {
      if ((r.parentId === null || r.parentId === undefined) && String(r.id).includes('.')) {
        const inferred = inferParentFromId(r.id);
        if (inferred !== null) {
          const pk = String(inferred);
          if (!childRelations.has(pk)) childRelations.set(pk, []);
          childRelations.get(pk).push({ 
            id: r.id, 
            name: r.name || `Subtask ${r.id}`, 
            energy: r.energy ?? null,
            category: r.category ?? null,
            delegationQuadrant: r.delegationQuadrant ?? null,
            delegateTo: r.delegateTo ?? r.assignedTo ?? "",
            delegationConstraint: r.delegationConstraint ?? r.constraint ?? ""
          });
          return;
        }
      }
      if ((r.parentId === null || r.parentId === undefined) && (!r.nested || r.nested.length === 0)) {
        if (/^\d+\.\d+$/.test(String(r.name).trim())) {
          const parts = String(r.name).trim().split('.');
          const pk = parts[0];
          if (!childRelations.has(pk)) childRelations.set(pk, []);
          childRelations.get(pk).push({ 
            id: r.id, 
            name: r.name, 
            energy: r.energy ?? null,
            category: r.category ?? null,
            delegationQuadrant: r.delegationQuadrant ?? null,
            delegateTo: r.delegateTo ?? r.assignedTo ?? "",
            delegationConstraint: r.delegationConstraint ?? r.constraint ?? ""
          });
        }
      }
    });

    childRelations.forEach((childrenArr, parentKey) => {
      if (!nodeMap.has(parentKey)) {
        const maybeParentRaw = raw.find(rr => String(rr.id) === parentKey || String(rr.name) === parentKey);
        const placeholderName = maybeParentRaw ? (maybeParentRaw.name || `Task ${maybeParentRaw.id}`) : `Task ${parentKey}`;
        nodeMap.set(parentKey, { 
          id: isNaN(Number(parentKey)) ? parentKey : Number(parentKey), 
          name: placeholderName, 
          energy: null,
          category: null,
          delegationQuadrant: null,
          delegateTo: "",
          delegationConstraint: "",
          subtasks: [], 
          _isPlaceholder: true 
        });
      }
      const node = nodeMap.get(parentKey);
      node.subtasks = node.subtasks.concat(childrenArr);
    });

    const childIds = new Set();
    childRelations.forEach(children => children.forEach(c => childIds.add(String(c.id))));

    const topLevel = [];
    nodeMap.forEach((node) => {
      if (!childIds.has(String(node.id))) {
        topLevel.push({ 
          id: node.id, 
          name: node.name ?? `Task ${node.id}`, 
          energy: typeof node.energy === "number" ? node.energy : (node.energy === null ? null : Number(node.energy)), 
          category: node.category ?? null,
          delegationQuadrant: node.delegationQuadrant ?? null,
          delegateTo: node.delegateTo ?? node.assignedTo ?? "",
          delegationConstraint: node.delegationConstraint ?? node.constraint ?? "",
          subtasks: Array.isArray(node.subtasks) ? node.subtasks : [] 
        });
      }
    });

    topLevel.sort((a, b) => {
      const na = Number(a.id), nb = Number(b.id);
      if (isFinite(na) && isFinite(nb)) return na - nb;
      return String(a.id).localeCompare(String(b.id));
    });
    
    topLevel.forEach(t => {
      if (Array.isArray(t.subtasks)) {
        t.subtasks.sort((s1, s2) => {
          const sa = parseFloat(String(s1.id)), sb = parseFloat(String(s2.id));
          if (isFinite(sa) && isFinite(sb)) return sa - sb;
          return String(s1.id).localeCompare(String(s2.id));
        });
      }
    });

    return { tasks: topLevel, didAssignIds };
  }, []);


  // const computeTaskEnergy = (task) => {
  //   if (Array.isArray(task.subtasks) && task.subtasks.length > 0) {
  //     return task.subtasks.reduce((sum, sub) => {
  //       const val = Number.isFinite(sub.energy) ? sub.energy : 0;
  //       return sum + val;
  //     }, 0);
  //   }
  //   return Number.isFinite(task.energy) ? task.energy : null;
  // };

  const sanitizeForSave = (arr) => (arr || []).map(t => {
    const subtasks = (t.subtasks || []).map(s => ({ 
      id: s.id, 
      name: s.name, 
      energy: s.energy === undefined ? null : s.energy,
      category: s.category ?? null,
      delegationQuadrant: s.delegationQuadrant ?? null,
      delegateTo: s.delegateTo ?? s.assignedTo ?? "",
      delegationConstraint: s.delegationConstraint ?? s.constraint ?? ""
    }));
    const energy = Array.isArray(subtasks) && subtasks.length > 0
      ? subtasks.reduce((sum, sub) => sum + (Number.isFinite(sub.energy) ? sub.energy : 0), 0)
      : (t.energy === undefined ? null : t.energy);

    return { 
      id: t.id, 
      name: t.name, 
      energy,
      category: t.category ?? null,
      delegationQuadrant: t.delegationQuadrant ?? null,
      delegateTo: t.delegateTo ?? t.assignedTo ?? "",
      delegationConstraint: t.delegationConstraint ?? t.constraint ?? "",
      subtasks,
    };
  });


  const [tasks, setTasks] = useState(() => {
    if (Array.isArray(initialTasks) && initialTasks.length > 0) {
      console.log("🎯 Initial state from initialTasks:", initialTasks);
      hasLoadedInitialData.current = true;
      const normalized = normalizeLoaded(initialTasks);
      const numericMainMax = Math.max(0, ...normalized.tasks.map(t => { const n = Number(t.id); return isFinite(n) ? n : 0; }));
      nextMainIdRef.current = Math.max(nextMainIdRef.current, numericMainMax + 1);
      normalized.tasks.forEach(t => (t.subtasks || []).forEach(s => { const sNum = Number(s.id); if (isFinite(sNum)) nextSubIdRef.current = Math.max(nextSubIdRef.current, sNum + 1); }));
      return normalized.tasks;
    }

    console.log("🆕 Initial state: demo tasks");
    return buildDemo();
  });

  const [expandedTasks, setExpandedTasks] = useState(new Set());
  const [hideSubtaskLabel, setHideSubtaskLabel] = useState(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [focusedInput, setFocusedInput] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [warningShown, setWarningShown] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, taskId: null, energy: 0 });

  const toastCounter = useRef(0);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);


  const showToast = useCallback(
  (message, type = "info", timeout = 3500) => {
    const id = ++toastCounter.current;

    setToasts(prev => [
      ...prev,
      { id, message, type }
    ]);

    if (timeout > 0) {
      setTimeout(() => removeToast(id), timeout);
    }
  },
  [setToasts, removeToast]
);

  // const showToast = (message, type = 'info', timeout = 3500) => { const id = ++toastCounter.current; setToasts(prev => [...prev, { id, message, type }]); if (timeout > 0) setTimeout(() => removeToast(id), timeout); };
  // const removeToast = (id) => setToasts(prev => prev.filter(t => t.id !== id));

  const calculateTotalEnergy = (list = tasks) => list.reduce((acc, t) => {
    const hasSubtasks = Array.isArray(t.subtasks) && t.subtasks.length > 0;
    const main = !hasSubtasks && Number.isFinite(t.energy) ? t.energy : 0;
    const subs = (t.subtasks || []).reduce((s, st) => s + (Number.isFinite(st.energy) ? st.energy : 0), 0);
    return acc + main + subs;
  }, 0);
  const totalEnergySpent = calculateTotalEnergy();
  const energyLeft = Math.max(0, 100 - totalEnergySpent);
  const isEnergyFull = totalEnergySpent >= 100;

  const saveProgressOnly = useCallback(async (saveObj, status = "in_progress") => {
    console.log("💾 Saving progress:", saveObj);
    try {
      const savedData = await progressService.saveProgress(
        userId,
        subsectionId,
        saveObj,
        status
      );
      console.log("✅ Progress saved successfully");
      return savedData;
    } catch (err) {
      console.error("❌ Save failed:", err);
      throw err;
    }
  }, [userId, subsectionId]);

  useEffect(() => {
    if (hasLoadedInitialData.current) return;
    if (Array.isArray(initialTasks) && initialTasks.length > 0) return;

    let mounted = true;
    (async () => {
      try {
        const saved = await progressService.getUserProgress(userId, subsectionId);
        if (!mounted) return;

        let savedTasks =
          saved?.data?.tasks ??
          (saved?.data ?? {}).progress?.tasks ??
          saved?.tasks ??
          null;
        if (Array.isArray(savedTasks) && savedTasks.length > 0) {
          const normalized = normalizeLoaded(savedTasks);
          const numericMainMax = Math.max(0, ...normalized.tasks.map(t => { const n = Number(t.id); return isFinite(n) ? n : 0; }));
          nextMainIdRef.current = Math.max(nextMainIdRef.current, numericMainMax + 1);
          normalized.tasks.forEach(t => (t.subtasks || []).forEach(s => { const sNum = Number(s.id); if (isFinite(sNum)) nextSubIdRef.current = Math.max(nextSubIdRef.current, sNum + 1); }));
          setTasks(normalized.tasks);
          if (normalized.didAssignIds) {
            await saveProgressOnly({ tasks: normalized.tasks }, "in_progress");
          }
          hasLoadedInitialData.current = true;
        }
      } catch (err) {
        console.error("❌ Failed to load saved tasks:", err);
      } finally {
        skipInitialAutoSave.current = false;
      }
    })();
    return () => { mounted = false; };
  }, [userId, subsectionId, initialTasks, normalizeLoaded, saveProgressOnly]);

  useEffect(() => {
    if (!initialTasks || !Array.isArray(initialTasks) || initialTasks.length === 0) return;

    const normalized = normalizeLoaded(initialTasks);
    const numericMainMax = Math.max(0, ...normalized.tasks.map(t => { const n = Number(t.id); return isFinite(n) ? n : 0; }));
    nextMainIdRef.current = Math.max(nextMainIdRef.current, numericMainMax + 1);
    normalized.tasks.forEach(t => (t.subtasks || []).forEach(s => { const sNum = Number(s.id); if (isFinite(sNum)) nextSubIdRef.current = Math.max(nextSubIdRef.current, sNum + 1); }));
    setTasks(normalized.tasks);
    if (normalized.didAssignIds) {
      saveProgressOnly({ tasks: normalized.tasks }, "in_progress").catch(() => {});
    }
    skipInitialAutoSave.current = false;
    hasLoadedInitialData.current = true;
  }, [initialTasks, userId, subsectionId, normalizeLoaded, saveProgressOnly]);

  useEffect(() => {
    const sanitized = sanitizeForSave(tasks);
    onDataChange?.({ tasks: sanitized });
  }, [tasks, onDataChange]);

  useEffect(() => {
    if (skipInitialAutoSave.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        const sanitized = sanitizeForSave(tasks);
        const dataToSave = {
          tasks: sanitized,
        };
        await saveProgressOnly(dataToSave, "in_progress");
      } catch (err) {
        console.error("Auto-save failed", err);
        showToast("Auto-save failed", "error");
      }
    }, 900);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [tasks, userId, subsectionId, saveProgressOnly, showToast]);

  const getNextMainId = () => generateGuid();
  const getNextSubId = () => generateGuid();

  useEffect(() => {
    if (!focusedInput) return;
    const key = focusedInput.type === "main-name" ? `name-${focusedInput.taskId}` : focusedInput.type === "sub-name" ? `name-${focusedInput.taskId}-${focusedInput.subId}` : focusedInput.type === "main" ? `energy-${focusedInput.taskId}` : focusedInput.type === "sub" ? `energy-${focusedInput.taskId}-${focusedInput.subId}` : null;
    if (!key) return;
    const ref = nameInputRefs.current.get(key) || energyInputRefs.current.get(key);
    if (ref && typeof ref.focus === "function") setTimeout(() => { try { ref.focus(); ref.select?.(); } catch (e) {} }, 0);
  }, [focusedInput]);

  const addMainTask = () => { 
    if (isEnergyFull) {
      if (!warningShown) {
        showToast("⚠️ Energy is 100% or more. Cannot add new tasks.", "error");
        setWarningShown(true);
      }
      return;
    }
    const id = getNextMainId(); 
    setTasks(prev => [...prev, { id, name: "", energy: null, category: null, delegationQuadrant: null, delegateTo: "", delegationConstraint: "", subtasks: [], _justCreated: true }]); 
    setTimeout(() => setFocusedInput({ type: 'main-name', taskId: id }), 0); 
  };

  const performAddSubtask = (taskId) => {
    setTasks(prev => prev.map(t => t.id !== taskId ? t : { 
      ...t, 
      energy: null,
      subtasks: [...(t.subtasks || []), { id: getNextSubId(), name: "", energy: null, category: null, delegationQuadrant: null, delegateTo: "", delegationConstraint: "", _justCreated: true }] 
    })); 
    setExpandedTasks(prev => { const next = new Set(prev); next.add(taskId); return next; }); 
    setHideSubtaskLabel(prev => { const next = new Set(prev); next.add(taskId); return next; }); 
    setTimeout(() => setFocusedInput({ type: 'sub-name', taskId, subId: null }), 0); 
  };

  const addSubtask = (taskId) => { 
    if (isEnergyFull) {
      if (!warningShown) {
        showToast("⚠️ Energy is 100% or more. Cannot add new subtasks.", "error");
        setWarningShown(true);
      }
      return;
    }

    const task = tasks.find(t => t.id === taskId);
    if (task && Number.isFinite(task.energy) && task.energy > 0) {
      setConfirmModal({ isOpen: true, taskId, energy: task.energy });
    } else {
      performAddSubtask(taskId);
    }
  };

  const handleConfirmAddSubtask = () => {
    performAddSubtask(confirmModal.taskId);
    setConfirmModal({ isOpen: false, taskId: null, energy: 0 });
  };

  const handleCancelAddSubtask = () => {
    setConfirmModal({ isOpen: false, taskId: null, energy: 0 });
  };

  useEffect(() => {
    if (!focusedInput) return;
    if (focusedInput.type === 'sub-name' && focusedInput.subId == null) {
      const task = tasks.find(t => t.id === focusedInput.taskId); if (!task) return; const last = task.subtasks && task.subtasks.length > 0 ? task.subtasks[task.subtasks.length - 1].id : null; if (last) setFocusedInput({ type: 'sub-name', taskId: focusedInput.taskId, subId: last });
    }
  }, [tasks, focusedInput]);

  const updateTaskName = (taskId, name) => setTasks(prev => prev.map(t => t.id === taskId ? { ...t, name, _justCreated: false, _isDemoPlaceholder: false } : t));
  
  const updateTaskEnergy = (taskId, rawValue) => {
    const task = tasks.find(t => t.id === taskId); 
    if (task && (task.subtasks || []).length > 0) return;
    
    const digits = String(rawValue || "").replace(/[^\d]/g, "");
    const parsed = digits === "" ? null : Math.min(100, Math.max(0, parseInt(digits, 10)));
    
    setTasks(prev => {
      const otherTotal = prev.reduce((acc, t) => { 
        if (t.id === taskId) return acc; 
        const hasSubs = Array.isArray(t.subtasks) && t.subtasks.length > 0;
        const main = !hasSubs && Number.isFinite(t.energy) ? t.energy : 0; 
        const subs = (t.subtasks || []).reduce((s, st) => s + (Number.isFinite(st.energy) ? st.energy : 0), 0); 
        return acc + main + subs; 
      }, 0);
      
      const thisTask = prev.find(t => t.id === taskId);
      const thisSubSum = (thisTask?.subtasks || []).reduce((s, st) => s + (Number.isFinite(st.energy) ? st.energy : 0), 0);
      
      if (parsed !== null && otherTotal + parsed + thisSubSum > 100) {
        const remaining = 100 - otherTotal - thisSubSum;
        showToast(`⚠️ Cannot assign ${parsed}% energy. Only ${remaining}% remaining (Total would exceed 100%)`, "error");
        return prev;
      }
      
      return prev.map(t => t.id === taskId ? { ...t, energy: parsed } : t);
    });
  };

  const updateSubtaskName = (taskId, subId, name) => setTasks(prev => prev.map(t => t.id === taskId ? { ...t, subtasks: (t.subtasks || []).map(s => s.id === subId ? { ...s, name, _justCreated: false } : s) } : t));
  
  const updateSubtaskEnergy = (taskId, subId, rawValue) => {
    const digits = String(rawValue || "").replace(/[^\d]/g, "");
    const parsed = digits === "" ? null : Math.min(100, Math.max(0, parseInt(digits, 10)));
    
    setTasks(prev => {
      const otherTotal = prev.reduce((acc, t) => {
        const hasSubs = Array.isArray(t.subtasks) && t.subtasks.length > 0;
        if (t.id !== taskId) { 
          const main = !hasSubs && Number.isFinite(t.energy) ? t.energy : 0; 
          const subs = (t.subtasks || []).reduce((s, st) => s + (Number.isFinite(st.energy) ? st.energy : 0), 0); 
          return acc + main + subs; 
        }
        const subsOther = (t.subtasks || []).filter(s => s.id !== subId).reduce((ss, s) => ss + (Number.isFinite(s.energy) ? s.energy : 0), 0); 
        return acc + subsOther; 
      }, 0);
      
      if (parsed !== null && otherTotal + parsed > 100) {
        const remaining = 100 - otherTotal;
        showToast(`⚠️ Cannot assign ${parsed}% energy. Only ${remaining}% remaining (Total would exceed 100%)`, "error");
        return prev;
      }
      
      return prev.map(t => t.id === taskId ? { 
        ...t, 
        energy: null,
        subtasks: (t.subtasks || []).map(s => s.id === subId ? { ...s, energy: parsed } : s) 
      } : t);
    });
  };

  const deleteTask = (taskId) => { setTasks(prev => prev.filter(t => t.id !== taskId)); setExpandedTasks(prev => { const next = new Set(prev); next.delete(taskId); return next; }); setHideSubtaskLabel(prev => { const next = new Set(prev); next.delete(taskId); return next; }); };
  const deleteSubtask = (taskId, subId) => { setTasks(prev => prev.map(t => t.id === taskId ? { ...t, subtasks: (t.subtasks || []).filter(s => s.id !== subId) } : t)); };
  const toggleExpand = (taskId) => setExpandedTasks(prev => { const next = new Set(prev); next.has(taskId) ? next.delete(taskId) : next.add(taskId); return next; });
  const countSubtasks = (task) => (task.subtasks || []).length;
  const isTaskActive = (task, isExpanded) => { if (isExpanded) return true; if (Number.isFinite(task.energy) && task.energy > 0) return true; if ((task.subtasks || []).some(s => Number.isFinite(s.energy) && s.energy > 0)) return true; return false; };

  const sumSubtasks = (task) => (task.subtasks || []).reduce((s, st) => s + (Number.isFinite(st.energy) ? st.energy : 0), 0);

  // const buildClassifyTasks = (currentTasks = []) => {
  //   const out = [];
  //   currentTasks.forEach((t) => {
  //     if (Array.isArray(t.subtasks) && t.subtasks.length > 0) {
  //       t.subtasks.forEach((s) => {
  //         if (!Number.isFinite(s.energy) || s.energy <= 0) return;
  //         out.push({
  //           id: s.id,
  //           name: s.name ?? `Subtask ${s.id}`,
  //           energy: s.energy ?? null,
  //           subtasks: [],
  //           ...(s.category !== undefined ? { category: s.category } : {}),
  //           ...(s.delegationQuadrant !== undefined ? { delegationQuadrant: s.delegationQuadrant } : {}),
  //           ...(s.delegateTo !== undefined ? { delegateTo: s.delegateTo } : {}),
  //           ...(s.delegationConstraint !== undefined ? { delegationConstraint: s.delegationConstraint } : {}),
  //         });
  //       });
  //     } else {
  //       if (!Number.isFinite(t.energy) || t.energy <= 0) return;
  //       out.push({
  //         id: t.id,
  //         name: t.name ?? `Task ${t.id}`,
  //         energy: t.energy ?? null,
  //         subtasks: [],
  //         ...(t.category !== undefined ? { category: t.category } : {}),
  //         ...(t.delegationQuadrant !== undefined ? { delegationQuadrant: t.delegationQuadrant } : {}),
  //         ...(t.delegateTo !== undefined ? { delegateTo: t.delegateTo } : {}),
  //         ...(t.delegationConstraint !== undefined ? { delegationConstraint: t.delegationConstraint } : {}),
  //       });
  //     }
  //   });
  //   return out;
  // };

  const handleGoToNext = async () => {
    setIsSaving(true);
    try {
      const sanitized = sanitizeForSave(tasks);
        const dataToSave = {
          tasks: sanitized,
        };

      await saveProgressOnly(dataToSave, "in_progress");
      onDataChange?.({ tasks: sanitized });

      showToast("Saved and moving to Classify buckets", "info", 1500);

      onNextStep?.();
    } catch (err) {
      console.error("Failed to save progress or move next", err);
      showToast("Failed to save. Try again.", "error");
      setIsSaving(false);
      return;
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (!isEnergyFull) {
      setWarningShown(false);
    }
  }, [isEnergyFull]);

  return (
    <div className="overflow-y-auto min-h-[600px]">
      <Toast toasts={toasts} removeToast={removeToast} />
      
      <ConfirmModal 
        isOpen={confirmModal.isOpen}
        onConfirm={handleConfirmAddSubtask}
        onCancel={handleCancelAddSubtask}
        energy={confirmModal.energy}
      />

      <div className="max-w-[1200px] mx-auto p-6 bg-white rounded-2xl">
        <div className="flex gap-10">
          <div className="flex-1">
            <div className="space-y-2">
              {tasks.map((task) => {
                const isExpanded = expandedTasks.has(task.id);
                const hasSubtasks = (task.subtasks || []).length > 0;
                const active = isTaskActive(task, isExpanded);
                const subtasksSum = sumSubtasks(task);
                const displayMainEnergy = hasSubtasks
                  ? subtasksSum
                  : (Number.isFinite(task.energy) ? task.energy : 0);
                const mainPlaceholder = `Main Task ${task.id}`;
                const showAddPlaceholder = !!task._justCreated;
                
                // const isTyping = focusedInput && focusedInput.type === 'main-name' && focusedInput.taskId === task.id;
                // const shouldShowPlaceholder = task.name === "" && !isTyping;
                // const placeholderText = shouldShowPlaceholder ? (showAddPlaceholder ? "Add a Main Task" : mainPlaceholder) : undefined;
                
                const isTyping = focusedInput && focusedInput.type === 'main-name' && focusedInput.taskId === task.id;
                const shouldShowPlaceholder = (task.name === "" || task._isDemoPlaceholder) && !isTyping;
                const placeholderText = shouldShowPlaceholder ? (showAddPlaceholder ? "Add a Main Task" : mainPlaceholder) : undefined;

                // const getMainTextColor = () => {
                //   if (task.name === "") return "#CCCCCC";
                //   if (task._isDemoPlaceholder) return "#CCCCCC";
                //   if (task.name && task.name.trim() !== "") return "#54B0AF";
                //   return "#CCCCCC";
                // };

                const getMainTextColor = () => {
                  if (task.name === "" || task._isDemoPlaceholder) return "#CCCCCC";
                  if (task.name && task.name.trim() !== "") return "#54B0AF";
                  return "#CCCCCC";
                };

                return (
                  <div key={task.id} className="mb-2">
                    <div className="flex items-center gap-4 py-3 group hover:bg-gray-50 px-3 border-b border-gray-200">
                      <button onClick={() => hasSubtasks && toggleExpand(task.id)} disabled={!hasSubtasks} className={`${hasSubtasks ? 'cursor-pointer' : 'cursor-default opacity-40'}`} aria-label="toggle subtasks">
                        {isExpanded ? <ArrowTriangle direction="down" /> : <ArrowTriangle direction="right" />}
                      </button>

                      <input ref={(el) => { const key = `name-${task.id}`; if (el) nameInputRefs.current.set(key, el); else nameInputRefs.current.delete(key); }}
                             type="text" value={task.name} onChange={(e) => updateTaskName(task.id, e.target.value)} placeholder={placeholderText}
                            //  onFocus={(e) => { 
                            //    setFocusedInput({ type: 'main-name', taskId: task.id }); 
                            //    if (task._isDemoPlaceholder) { 
                            //      setTimeout(() => e.target.select(), 0); 
                            //    }
                            //  }} 
                            onFocus={(e) => { 
                              setFocusedInput({ type: 'main-name', taskId: task.id }); 
                              if (task._isDemoPlaceholder) { 
                                updateTaskName(task.id, "");
                                setTimeout(() => e.target.focus(), 0); 
                              }
                            }}
                             onBlur={() => setFocusedInput(null)}
                             style={{ color: getMainTextColor() }}
                             className={`flex-1 px-1 py-1 text-[15px] border-none outline-none bg-transparent ${isExpanded && hasSubtasks ? 'font-medium' : ''}`} />

                      <span className="text-[12px] text-[#CCCCCC] min-w-[30px] text-center flex-shrink-0">{hasSubtasks ? `${countSubtasks(task) < 10 ? '0' : ''}${countSubtasks(task)}` : ""}</span>

                      <button onClick={() => addSubtask(task.id)} disabled={isEnergyFull} className="flex items-center gap-2 px-3 py-1.5 rounded text-[12px] transition-colors" style={{ color: isEnergyFull ? undefined : (active ? '#54B0AF' : '#CCCCCC') }}>
                        {!hideSubtaskLabel.has(task.id) && <span>Subtask</span>}
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center ${isEnergyFull ? 'bg-[#DAF3F3]' : 'bg-[#EDEDED]'}`}><FiGitMerge className="w-3.5 h-3.5" /></div>
                      </button>

                      <div className="flex items-center gap-2 flex-shrink-0 relative">
                        <input ref={(el) => { const key = `energy-${task.id}`; if (el) energyInputRefs.current.set(key, el); else energyInputRefs.current.delete(key); }}
                               type="text" inputMode="numeric" pattern="[0-9]*"
                               value={hasSubtasks ? String(displayMainEnergy) : (task.energy === null ? "" : String(task.energy))}
                               onChange={(e) => updateTaskEnergy(task.id, e.target.value)}
                               placeholder={focusedInput && focusedInput.type === 'main' && focusedInput.taskId === task.id ? "" : "% Energy"}
                               disabled={(isEnergyFull && !Number.isFinite(task.energy)) || hasSubtasks}
                               readOnly={hasSubtasks}
                               onFocus={() => setFocusedInput({ type: 'main', taskId: task.id })} onBlur={() => setFocusedInput(null)}
                               className="w-24 p-2 text-[14px] text-center rounded outline-none bg-transparent" />
                        <span className="pointer-events-none">%</span>
                        <SlEnergy className="w-4 h-4 text-[#585858]" />
                      </div>

                      <button onClick={() => deleteTask(task.id)} className="text-gray-300 hover:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-4 h-4" /></button>
                    </div>

                    {/* ✅ PERFECT SUBTASK SECTION WITH CONNECTORS */}
                    {isExpanded && (task.subtasks || []).length > 0 && (
                      <div className="relative pl-12 pr-3">
                        {/* ✅ Vertical gray line */}
                        <div className="absolute left-8 top-0 bottom-0 w-[2px] bg-[#CCCCCC]" style={{ height: `calc(100% - 22px)` }}  />
                        
                        {(task.subtasks || []).map((sub, index) => {
                          const subPlaceholder = `Subtask ${index + 1}`;
                          const subShowAdd = !!sub._justCreated;
                          
                          const isSubTyping = focusedInput && focusedInput.type === 'sub-name' && focusedInput.taskId === task.id && focusedInput.subId === sub.id;
                          const subPlaceholderText = sub.name === "" && !isSubTyping ? (subShowAdd ? "Add a Subtask" : subPlaceholder) : undefined;
                          
                          const getSubTextColor = () => {
                            if (sub.name === "") return "#CCCCCC";
                            if (sub.name && sub.name.trim() !== "") return "#54B0AF";
                            return "#CCCCCC";
                          };

                          return (
                            <div key={sub.id} className="flex items-center gap-2 py-3 group hover:bg-gray-50 px-3 border-b border-gray-100 relative">
                              {/* ✅ Horizontal gray line from vertical line */}
                              <div className="absolute left-[-16px] top-1/2 w-[20px] h-[2px] bg-[#CCCCCC]" style={{ transform: 'translateY(-50%)' }} >
                              <MdKeyboardArrowRight className="absolute left-[45%] -top-[9px] text-xl text-[#CCCCCC]" />
                              </div>
                              
                              {/* ✅ Gray arrow pointing right */}
                              <div style={{ 
                                width: 0, 
                                height: 0, 
                                borderTop: '5px solid transparent', 
                                borderBottom: '5px solid transparent', 
                                borderLeft: '8px solid #000000',
                                flexShrink: 0
                              }} className="ml-2" />
                              
                              <input ref={(el) => { const key = `name-${task.id}-${sub.id}`; if (el) nameInputRefs.current.set(key, el); else nameInputRefs.current.delete(key); }}
                                     type="text" value={sub.name} onChange={(e) => updateSubtaskName(task.id, sub.id, e.target.value)} placeholder={subPlaceholderText}
                                     onFocus={() => setFocusedInput({ type: 'sub-name', taskId: task.id, subId: sub.id })} onBlur={() => setFocusedInput(null)}
                                     style={{ color: getSubTextColor() }}
                                     className="flex-1 px-1 py-1 text-[14px] border-none outline-none bg-transparent" />
                              <div className="w-[120px] flex-shrink-0" />
                              <div className="min-w-[30px] flex-shrink-0" />
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <input ref={(el) => { const key = `energy-${task.id}-${sub.id}`; if (el) energyInputRefs.current.set(key, el); else energyInputRefs.current.delete(key); }}
                                       type="text" inputMode="numeric" pattern="[0-9]*"
                                       value={sub.energy === null ? "" : String(sub.energy)}
                                       onChange={(e) => updateSubtaskEnergy(task.id, sub.id, e.target.value)}
                                       placeholder={focusedInput && focusedInput.type === 'sub' && focusedInput.taskId === task.id && focusedInput.subId === sub.id ? "" : "% Energy"}
                                       disabled={isEnergyFull && !Number.isFinite(sub.energy)}
                                       onFocus={() => setFocusedInput({ type: 'sub', taskId: task.id, subId: sub.id })} onBlur={() => setFocusedInput(null)}
                                       className="w-24 p-2 text-[14px] text-center rounded outline-none bg-transparent" />
                                <Zap className="w-4 h-4 text-[#585858]" />
                              </div>

                              <button onClick={() => deleteSubtask(task.id, sub.id)} className="text-gray-300 hover:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-4 h-4" /></button>
                            </div>
                          );
                        })}
                        
                        {/* ✅ "Add more subtasks" with connector */}
                        <div className="relative flex items-center gap-2 py-3 px-3">
                          {/* ✅ Horizontal gray line */}
                          <div className="absolute left-[-16px] top-1/2 w-[20px] h-[2px] bg-[#CCCCCC]" style={{ transform: 'translateY(-50%)' }} > 
                            <MdKeyboardArrowRight className="absolute left-[45%] -top-[9px] text-xl text-[#CCCCCC]" />
                          </div>
                          
                          {/* ✅ Plus icon as arrow */}
                          {/* <div style={{ 
                            width: 0, 
                            height: 0, 
                            borderTop: '5px solid transparent', 
                            borderBottom: '5px solid transparent', 
                            borderLeft: '8px solid #CCCCCC',
                            flexShrink: 0
                          }} /> */}
                          
                          <button 
                            onClick={() => addSubtask(task.id)} 
                            disabled={isEnergyFull} 
                            className={`text-[13px] flex items-center gap-2 ${isEnergyFull ? 'text-gray-300 cursor-not-allowed' : 'text-[#CCCCCC] hover:text-[#3AAFA9]'}`}
                          >
                            <Plus className="w-4 h-4" />
                            Add more subtasks
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <button onClick={addMainTask} disabled={isEnergyFull} className={`mt-6 text-[13px] flex items-center gap-2 ${isEnergyFull ? 'text-gray-300 cursor-not-allowed' : 'text-[#CCCCCC] hover:text-[#3AAFA9]'}`}>
              <Plus className="w-4 h-4" /> Add more tasks
            </button>

            <div className="mt-10 pt-4">
              <div className="flex justify-between items-center mb-8 border-t-2 border-b-2 py-4 lg:py-5 2xl:py-8 px-4" style={{ background: "linear-gradient(to bottom, #FFFFFF 0%, #EDF8F7E6 25%, #EDF8F7E6 75%, #FFFFFF 100%)" }}>
                <h3 className="text-[16px] lg:text-[18px] 2xl:text-[20px] font-medium text-black tracking-wider">TOTAL ENERGY SPEND</h3>
                <span className="text-[16px] lg:text-[18px] 2xl:text-[20px] font-medium text-black">{totalEnergySpent}% Spent</span>
              </div>

              <button onClick={handleGoToNext} disabled={isSaving} className={`px-8 py-4 font-gotham rounded-full font-medium text-[14px] md:text-lg flex items-center gap-3 transition-all ${isSaving ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-black text-white hover:bg-gray-800'}`}>
                {isSaving ? 'Saving...' : 'Go to Next Step'}
                <div className={`rounded-full p-1 bg-white`}><ChevronRight className={`w-3.5 h-3.5 text-black`} /></div>
              </button>
            </div>
          </div>

          <div className="w-[260px] flex flex-col items-center pt-4">
            <Silhouette progress={totalEnergySpent} />
            <div className="h-6 2xl:h-10 w-[2px] bg-black mt-6" />
            <div className="text-center mt-4">
              <div className="text-[13px] 2xl:text-[14px] uppercase font-medium text-[#454545] tracking-[0.15em] mb-2">ENERGY LEFT</div>
              <div className="text-[45px] 2xl:text-[56px] font-bold text-[#1DA4A2] leading-none">{energyLeft}%</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskEnergyAnalyzer;
